import {
  Typography,
  Steps,
  Card,
  Button,
  Row,
  Col,
  Tag,
  message,
  Spin,
  Space,
  Tabs,
} from "antd";
import { SendOutlined } from "@ant-design/icons";
import EnquiryActivityLog from "./Components/EnquiryActivityLog.jsx";
import CustomButton from "../../components/CustomButton/Button";
import PrimaryButton from "../../components/CustomButton/PrimaryButton";
import BackButton from "../../components/CustomButton/BackButton";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getEnquiryManagementView,
  activateEnquiry,
  jvRejectEnquiry,
} from "../../redux/slices/EnqMngtSlice";
import { getBusDetailedView } from "../../redux/slices/BusSlice";
import { useResetTemporaryPassword } from "../../hooks/useResetTemporaryPassword";
import { count } from "../../redux/slices/dealsSlice";
import { count as allCount } from "../../redux/slices/AuthSlice";
import {
  sendNdaESignRequest,
  getNdaEsignStatus,
  sendSlaESignRequest,
  getSlaEsignStatus,
  getEsignStatusByOrgId,
} from "../../redux/slices/EsignSlice";
import { useState, useEffect } from "react";
import ReasonForm from "../../components/CustomForms/ReasonForm";
import {
  REASON_MODAL_CONFIG,
  ENQUIRY_SUCCESS_MESSAGES,
} from "../../constants/constants";
import CustomModal from "../../components/CustomModal/CustomModal";
import {
  getKycStatusLabel,
  getKycStatusTagColor,
} from "../../helpers/kycStatus";

const { Title, Text } = Typography;

const steps = ["Basic Information", "Onboarding", "Approval"];

const ViewBusinessDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId: routeOrgId } = useParams();
  const org = location.state?.org;
  const selectedStatus = location.state?.selectedStatus;
  const orgId = org?.orgId || routeOrgId;
  const dispatch = useDispatch();
  const resetTemporaryPassword = useResetTemporaryPassword();

  const { commentSubmitLoading, rejectSubmitError, rejectSubmitLoading } =
    useSelector((state) => state.enqMngt);
  const { busDetailedViewData } = useSelector((state) => state.business);
  const {
    ndaAgreementData,
    ndaAgreementLoading,
    ndaStatusLoading,
    slaAgreementLoading,
    slaAgreementData,
    slaStatusLoading,
    StatusData,
    StatusLoading,
  } = useSelector((state) => state.esign);

  const [currentStep, setCurrentStep] = useState(0);

  const [modal, setModal] = useState({
    type: null,
    data: null,
  });

  const openModal = (type, data = null) => {
    setModal({ type, data });
  };

  const closeModal = () => {
    setModal({ type: null, data: null });
  };

  const handleReject = () => {
    openModal("reject");
  };

  const handleResetPassword = () => {
    if (!org?.orgId) return;
    resetTemporaryPassword({ orgId: org.orgId });
  };

  useEffect(() => {
    if (rejectSubmitError) {
      message.error(
        rejectSubmitError?.message ||
          rejectSubmitError ||
          "Something went wrong",
      );
    }
  }, [rejectSubmitError]);

  useEffect(() => {
    if (orgId) {
      dispatch(
        getBusDetailedView({
          orgId,
          dealCategoryId: org?.dealCategoryId,
        }),
      );
    }
  }, [dispatch, orgId, org?.dealCategoryId]);

  useEffect(() => {
    if (currentStep === 1 && orgId) {
      dispatch(getEsignStatusByOrgId(orgId));
    }
  }, [currentStep, orgId, dispatch]);

  // 📌 reusable card
  const DocumentCard = ({
    title,
    desc,
    buttonText,
    onClick,
    loading,
    extraButton,
    status,
  }) => {
    const normalizedStatus = status;
    const isSigned = normalizedStatus === "signed";

    return (
      <Card style={{ borderRadius: 10, marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <div>
            <Title level={5}>{title}</Title>
            <Text type="secondary">{desc}</Text>
          </div>

          <Tag
            color={
              normalizedStatus === "signed"
                ? "green"
                : normalizedStatus === "unsigned"
                  ? "gold"
                  : "gold"
            }
          >
            {normalizedStatus || "unsigned"}
          </Tag>
        </Row>

        {/* ✅ IF SIGNED */}
        {isSigned ? (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              textAlign: "center",
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: 8,
              fontWeight: 500,
              color: "#389e0d",
            }}
          >
            ✅ {title} Signed
          </div>
        ) : (
          <>
            {/* SEND BUTTON */}
            <Button
              icon={<SendOutlined />}
              block
              loading={loading}
              onClick={onClick}
              style={{ marginTop: 16 }}
            >
              {buttonText}
            </Button>

            {/* CHECK STATUS BUTTON */}
            {extraButton && (
              <Button
                block
                style={{ marginTop: 10 }}
                onClick={extraButton.onClick}
                loading={extraButton.loading}
              >
                {extraButton.text}
              </Button>
            )}
          </>
        )}
      </Card>
    );
  };

  const handleApprove = async () => {
    if (!org?.orgId) return;
    const res = await dispatch(
      activateEnquiry({
        orgId: org.orgId,
        dealCategoryId: org.dealCategoryId,
      }),
    );
    if (res.payload?.statusCode === 201) {
      message.success(ENQUIRY_SUCCESS_MESSAGES.APPROVE_ENQUIRY);
      navigate(-1);
      dispatch(getEnquiryManagementView(selectedStatus));
      dispatch(count("ENQUIRYMGMT"));
      dispatch(allCount("ALL"));
      // closeModal();
    } else {
      message.error(res?.payload?.message || "Failed to approve enquiry");
    }
  };

  const handleSendNda = async () => {
    if (!org?.orgId) {
      message.warning("Organization ID missing");
      return;
    }

    try {
      const ndaStatus = StatusData?.jvNdaAgreement;
      const res = await dispatch(
        sendNdaESignRequest({
          orgId: org.orgId,
          document_id: ndaStatus?.document_id,
          docket_id: ndaStatus?.docket_id,
          stakeholder_id: ndaStatus?.stakeholder_id,
        }),
      );

      if (res.payload?.statusCode === 201) {
        const isResent = res.payload?.response?.resent;
        message.success(
          isResent
            ? "NDA signing link resent successfully"
            : "NDA document sent successfully",
        );
        dispatch(getEsignStatusByOrgId(org.orgId));
      } else {
        message.error(res.payload?.error || "Failed to send NDA document");
      }
    } catch (error) {
      message.error("Something went wrong while sending NDA");
    }
  };
  const handleCheckSignStatus = async () => {
    const ndaDocumentId =
      ndaAgreementData?.document_id || StatusData?.jvNdaAgreement?.document_id;
    const ndaDocketId =
      ndaAgreementData?.docket_id || StatusData?.jvNdaAgreement?.docket_id;

    if (!org?.orgId || !ndaDocumentId || !ndaDocketId) {
      message.warning("Required data missing");
      return;
    }

    try {
      const res = await dispatch(
        getNdaEsignStatus({
          orgId: org.orgId,
          document_id: ndaDocumentId,
          docket_id: ndaDocketId,
        }),
      );

      const status = res.payload?.statusCode;

      if (status === 201 && res.payload?.response?.status === "signed") {
        message.success("NDA Signed ✅");

        // ✅ Refresh eSign status
        dispatch(getEsignStatusByOrgId(org.orgId));
      } else {
        message.info("NDA is not signed yet.");
      }
    } catch (err) {
      message.error("Failed to check NDA status");
    }
  };

  const handleSendSla = async () => {
    if (!org?.orgId) {
      message.warning("Organization ID missing");
      return;
    }

    try {
      const slaStatus = StatusData?.jvSlaAgreement;
      const res = await dispatch(
        sendSlaESignRequest({
          orgId: org.orgId,
          document_id: slaStatus?.document_id,
          docket_id: slaStatus?.docket_id,
          stakeholder_id: slaStatus?.stakeholder_id,
        }),
      );

      if (res.payload?.statusCode === 201) {
        const isResent = res.payload?.response?.resent;
        message.success(
          isResent
            ? "SLA signing link resent successfully"
            : "SLA document sent successfully",
        );
        dispatch(getEsignStatusByOrgId(org.orgId));
      } else {
        message.error(res.payload?.error || "Failed to send SLA document");
      }
    } catch {
      message.error("Something went wrong while sending SLA");
    }
  };

  const handleCheckSlaStatus = async () => {
    const slaDocumentId =
      slaAgreementData?.document_id || StatusData?.jvSlaAgreement?.document_id;
    const slaDocketId =
      slaAgreementData?.docket_id || StatusData?.jvSlaAgreement?.docket_id;

    if (!org?.orgId || !slaDocumentId || !slaDocketId) {
      message.warning("Required data missing");
      return;
    }

    try {
      const res = await dispatch(
        getSlaEsignStatus({
          orgId: org.orgId,
          document_id: slaDocumentId,
          docket_id: slaDocketId,
        }),
      );

      if (
        res.payload?.statusCode === 201 &&
        res.payload?.response?.status === "signed"
      ) {
        message.success("SLA Agreement Signed ✅");

        // ✅ Refresh eSign status
        dispatch(getEsignStatusByOrgId(org.orgId));
      } else {
        message.info("SLA is not signed yet.");
      }
    } catch {
      message.error("Failed to check SLA status");
    }
  };

  const handleReasonSubmit = async (values) => {
    if (!org?.orgId) return;

    try {
      const res = await dispatch(
        jvRejectEnquiry({
          orgId: org.orgId,
          dealCategoryId: org.dealCategoryId,
          comments: values.reason, // form reason
        }),
      );

      if (res.payload?.statusCode === 201) {
        message.success(ENQUIRY_SUCCESS_MESSAGES.reject(org?.busName));

        closeModal();

        navigate(-1);
        dispatch(getEnquiryManagementView(selectedStatus));
        dispatch(count("ENQUIRYMGMT"));
        dispatch(allCount("ALL"));
      }
    } catch (error) {
      message.error("Something went wrong while rejecting enquiry");
    }
  };
  const isNdaSigned = StatusData?.jvNdaAgreement?.status === "signed";

  const isSlaSigned = StatusData?.jvSlaAgreement?.status === "signed";

  const isBothDocumentsSigned = isNdaSigned && isSlaSigned;

  const kycStatusLabel = getKycStatusLabel({
    kycDetails: busDetailedViewData?.kycDetails,
    statusName: org?.statusName,
  });
  const kycStatusColor = getKycStatusTagColor(kycStatusLabel);

  return (
    <div style={{ padding: 24 }}>
      {/* 🔙 Back */}
      <BackButton onClick={() => navigate(-1)}></BackButton>

      {/* 🏢 Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            {org?.busName || "Business Name"}
          </Title>
          <Space size="middle">
            <Text type="secondary">Business Onboarding Process</Text>
            <Tag color={kycStatusColor}>{kycStatusLabel}</Tag>
          </Space>
        </div>

        {/* <CustomButton type="primary" icon={<UserOutlined />}>
                    Assign
                </CustomButton> */}
      </div>

      <Tabs
        style={{ marginTop: 20 }}
        items={[
          {
            key: "1",
            label: "Onboarding Progress",
            children: (
              <>
                {/* 📊 Stepper */}
                <Card style={{ marginTop: 20, borderRadius: 10 }}>
                  <Title level={5}>Onboarding Progress</Title>

        <Steps
          current={currentStep}
          style={{ marginTop: 20 }}
          items={steps.map((step) => ({
            title: step,
          }))}
        />
      </Card>

      {/* ============================= */}
      {/* 🔽 STEP 0 - BASIC INFO */}
      {/* ============================= */}
      {currentStep === 0 && (
        <Row gutter={16} style={{ marginTop: 20 }}>
          {/* LEFT */}
          <Col span={24}>
            <Card
              title="Basic Information Collected"
              style={{ borderRadius: 10 }}
            >
              <Card
                size="small"
                style={{ marginBottom: 12, background: "#fafafa" }}
              >
                <Text type="secondary">Company Name</Text>
                <br />
                <Text strong>{org?.busName}</Text>
              </Card>

              <Card
                size="small"
                style={{ marginBottom: 12, background: "#fafafa" }}
              >
                <Text type="secondary">Contact Person</Text>
                <br />
                <Text strong>{org?.contactName}</Text>
              </Card>

              <Card
                size="small"
                style={{ marginBottom: 12, background: "#fafafa" }}
              >
                <Text type="secondary">Email</Text>
                <br />
                <Text>{org?.email}</Text>
              </Card>

              <Card
                size="small"
                style={{ marginBottom: 12, background: "#fafafa" }}
              >
                <Text type="secondary">Contact Phone NUmber</Text>
                <br />
                <Text strong>{org?.contactPhoneNumber}</Text>
              </Card>

              <Card
                size="small"
                style={{ marginBottom: 12, background: "#fafafa" }}
              >
                <Text type="secondary">Deal Type</Text>
                <br />
                <Tag color="cyan">
                  {org?.dealCategoryName || "Joint Ventures"}
                </Tag>
              </Card>

              <CustomButton
                type="primary"
                block
                size="large"
                onClick={() => setCurrentStep(1)}
              >
                Proceed to Onboarding
              </CustomButton>

              <CustomButton
                type="default"
                block
                size="large"
                onClick={handleResetPassword}
                style={{ marginTop: 12 }}
              >
                Reset Temporary Password
              </CustomButton>
            </Card>
          </Col>
        </Row>
      )}

      {/* ============================= */}
      {/* 🔽 STEP 1 - ONBOARDING SCREEN */}
      {/* ============================= */}
      {currentStep === 1 && (
        <Spin spinning={StatusLoading}>
          <div style={{ marginTop: 20 }}>
            <Title level={4}>Onboarding Documents</Title>
            <Text type="secondary">
              Send required documents to the business and assign to
              questionnaire stage
            </Text>

            <div style={{ marginTop: 20 }}>
              <DocumentCard
                title="SLA Document"
                desc="Service Level Agreement for business onboarding"
                buttonText={
                  StatusData?.jvSlaAgreement?.isSent
                    ? "Resend SLA Document"
                    : "Send SLA Document"
                }
                onClick={handleSendSla}
                loading={slaAgreementLoading}
                status={StatusData?.jvSlaAgreement?.status}
                extraButton={{
                  text: "Check Sign Status",
                  onClick: handleCheckSlaStatus,
                  loading: slaStatusLoading,
                }}
              />

              <DocumentCard
                title="NDA Document"
                desc="Non-Disclosure Agreement"
                buttonText={
                  StatusData?.jvNdaAgreement?.isSent
                    ? "Resend NDA Document"
                    : "Send NDA Document"
                }
                onClick={handleSendNda}
                loading={ndaAgreementLoading}
                status={StatusData?.jvNdaAgreement?.status}
                extraButton={{
                  text: "Check Sign Status",
                  onClick: handleCheckSignStatus,
                  loading: ndaStatusLoading,
                }}
              />
            </div>

            <Row
              justify="space-between"
              align="middle"
              style={{ marginTop: 20 }}
            >
              {/* ⬅️ LEFT - Previous */}
              <CustomButton
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={currentStep === 0}
                style={{ padding: "24px" }}
              >
                Previous
              </CustomButton>

              {/* ➡️ RIGHT - Approve / Reject */}
              <Space>
                <CustomButton
                  danger
                  style={{ padding: "24px" }}
                  onClick={handleReject}
                  loading={rejectSubmitLoading}
                >
                  Reject
                </CustomButton>
                <PrimaryButton
                  onClick={handleApprove}
                  loading={commentSubmitLoading}
                  disabled={!isBothDocumentsSigned}
                >
                  {isBothDocumentsSigned
                    ? "Approve"
                    : "Sign both documents to approve"}
                </PrimaryButton>
              </Space>
            </Row>
          </div>
        </Spin>
      )}
              </>
            ),
          },
          {
            key: "activity",
            label: "Activity Log",
            children: (
              <Card style={{ marginTop: 20, borderRadius: 10 }}>
                <EnquiryActivityLog orgId={orgId} dealCategoryId={org?.dealCategoryId} />
              </Card>
            ),
          },
        ]}
      />
      {modal.type && (
        <CustomModal
          title={REASON_MODAL_CONFIG[modal.type]?.title}
          open
          centered
          content={
            <ReasonForm
              label={REASON_MODAL_CONFIG[modal.type]?.label}
              placeholder="Write a valid reason"
              btnName={REASON_MODAL_CONFIG[modal.type]?.btnName}
              btnType={modal.type === "reject" ? "danger" : "primary"}
              submitLoading={rejectSubmitLoading}
              submitError={rejectSubmitError}
              onClose={closeModal}
              handleOnFormSubmit={handleReasonSubmit}
            />
          }
        />
      )}
    </div>
  );
};

export default ViewBusinessDetails;
